import {
  PROFILE_COLORS,
  PROFILE_SHADOWS,
  PROFILE_TYPOGRAPHY,
} from './constants';

function StatsIcon({ className }: { className?: string }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M15.3365 4.63105C15.2655 4.46009 15.1297 4.32424 14.9587 4.25324C14.8746 4.21739 14.7843 4.19837 14.6928 4.19727H11.1945C11.009 4.19727 10.831 4.27098 10.6998 4.40219C10.5686 4.5334 10.4949 4.71136 10.4949 4.89692C10.4949 5.08249 10.5686 5.26045 10.6998 5.39166C10.831 5.52287 11.009 5.59658 11.1945 5.59658H13.0067L9.09557 9.50768L6.79369 7.1988C6.72865 7.13322 6.65126 7.08117 6.566 7.04565C6.48074 7.01013 6.3893 6.99184 6.29693 6.99184C6.20457 6.99184 6.11312 7.01013 6.02786 7.04565C5.9426 7.08117 5.86522 7.13322 5.80017 7.1988L1.60222 11.3968C1.53664 11.4618 1.48459 11.5392 1.44907 11.6244C1.41355 11.7097 1.39526 11.8011 1.39526 11.8935C1.39526 11.9859 1.41355 12.0773 1.44907 12.1626C1.48459 12.2478 1.53664 12.3252 1.60222 12.3903C1.66726 12.4558 1.74465 12.5079 1.82991 12.5434C1.91517 12.5789 2.00662 12.5972 2.09898 12.5972C2.19134 12.5972 2.28279 12.5789 2.36805 12.5434C2.45331 12.5079 2.53069 12.4558 2.59574 12.3903L6.29693 8.68208L8.59881 10.991C8.66385 11.0565 8.74123 11.1086 8.82649 11.1441C8.91175 11.1796 9.0032 11.1979 9.09557 11.1979C9.18793 11.1979 9.27938 11.1796 9.36464 11.1441C9.4499 11.1086 9.52728 11.0565 9.59232 10.991L13.9932 6.5831V8.39522C13.9932 8.58078 14.0669 8.75874 14.1981 8.88995C14.3293 9.02116 14.5073 9.09488 14.6928 9.09488C14.8784 9.09488 15.0564 9.02116 15.1876 8.88995C15.3188 8.75874 15.3925 8.58078 15.3925 8.39522V4.89692C15.3914 4.80549 15.3724 4.71517 15.3365 4.63105Z"
        fill="#01A7C8"
      />
    </svg>
  );
}

export interface ActivityStats {
  thisWeek: number;
  thisMonth: number;
  thisYear: number;
}

interface SparksHistoryCardProps {
  stats?: ActivityStats | null;
}

/**
 * Get the current month name in uppercase (e.g. "IN MARCH").
 */
function getCurrentMonthLabel(): string {
  const monthName = new Date().toLocaleString('en-US', { month: 'long' });
  return `IN ${monthName.toUpperCase()}`;
}

/**
 * Get the current year label (e.g. "IN 2026").
 */
function getCurrentYearLabel(): string {
  return `IN ${new Date().getFullYear()}`;
}

/**
 * Format a stat value for display.
 * Returns "-" for 0, null, or undefined values.
 */
function formatStatValue(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) return '-';
  return String(value);
}

export default function SparksHistoryCard({ stats }: SparksHistoryCardProps) {
  const statItems = [
    { value: stats?.thisWeek, label: 'THIS WEEK', showIcon: true },
    { value: stats?.thisMonth, label: getCurrentMonthLabel(), showIcon: false },
    { value: stats?.thisYear, label: getCurrentYearLabel(), showIcon: false },
  ];

  return (
    <div
      className="rounded-2xl p-4"
      style={{
        backgroundColor: PROFILE_COLORS.cardBg,
        boxShadow: PROFILE_SHADOWS.sparksCard,
      }}
    >
      <h2
        className={`${PROFILE_TYPOGRAPHY.sparksTitle.weight} ${PROFILE_TYPOGRAPHY.sparksTitle.sizeClasses} mb-4`}
        style={{
          fontFamily: 'DM Sans, sans-serif',
          color: PROFILE_COLORS.textPrimary,
        }}
      >
        Sparks history
      </h2>
      <div className="flex justify-between">
        {statItems.map(({ value, label, showIcon }) => (
          <div key={label} className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1">
              <span
                className={
                  value === null || value === undefined || value === 0 ? 'mb-2 text-[20px]' :
                    `${PROFILE_TYPOGRAPHY.sparksStatNumber.weight} ${PROFILE_TYPOGRAPHY.sparksStatNumber.sizeClasses}`
                }
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  color: PROFILE_COLORS.textPrimary,
                }}
              >
                {formatStatValue(value)}
              </span>
              {showIcon && value && value > 0 ? (
                <StatsIcon className="w-[17px] h-[17px]" />
              ) : null}
            </div>
            <span
              className={`${PROFILE_TYPOGRAPHY.sparksStatLabel.weight} ${PROFILE_TYPOGRAPHY.sparksStatLabel.sizeClasses} ${PROFILE_TYPOGRAPHY.sparksStatLabel.extra}`}
              style={{
                fontFamily: 'DM Sans, sans-serif',
                color: PROFILE_COLORS.darkBlue,
              }}
            >
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
